import React from 'react';
import { useNavigate } from 'react-router-dom';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';

import {
  FilterButton,
  isFiltersSelected,
  SearchForm,
  SearchFormProps,
  TableContainer,
  TableHeader,
} from '@occc-app/design-system';
import { CampaignsTable } from '@occc-app/routes/Campaign/CampaignListPage/components/CampaignsTable';
import { useChainId } from '@occc-app/Chain/hooks';
import { deletePaginationParams, usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { useQueryParam } from '@occc-app/hooks/useQueryParam';
import { Publication_Status_Type } from '@occc/graphql';

import { Stack } from '@mui/material';
import { CampaignsFilterForm } from './CampaignsFilterForm';

import { useGetCampaignsWithCampaignComponents } from '../../CampaignDetailsPage/CampaignComponentsTable/useGetCampaignsWithCampaignComponents';
import { routeCreator as campaignRouteCreator } from '../../utils';
import { messages } from '../messages';
import { CampaignsFilterFormValues, CampaignStatusFilter } from '../types';

const STATUS_PARAM = 'status';
const PUBLICATION_STATUS_PARAM = 'publicationStatus';

export const CampaignsTableContainer = () => {
  const navigate = useNavigate();
  const chainId = useChainId();

  const [search, setSearch] = useSearchParams();
  const [paginationInfo, onPageChange] = usePaginationParams();

  const [statusFilter, setStatusFilter] = useQueryParam<CampaignStatusFilter>(
    (searchParams) => {
      const status = searchParams.get(STATUS_PARAM) || CampaignStatusFilter.Active;
      return status as CampaignStatusFilter;
    },
    (value, searchParams) => {
      if (value) {
        searchParams.set(STATUS_PARAM, value);
      } else {
        searchParams.delete(STATUS_PARAM);
      }

      deletePaginationParams(searchParams);
    },
  );

  const [publicationStatusFilter, setPublicationStatusFilter] =
    useQueryParam<Publication_Status_Type>(
      (searchParams) => {
        const publicationStatus =
          searchParams.get(PUBLICATION_STATUS_PARAM) || Publication_Status_Type.All;
        return publicationStatus as Publication_Status_Type;
      },
      (value, searchParams) => {
        if (value) {
          searchParams.set(PUBLICATION_STATUS_PARAM, value);
        } else {
          searchParams.delete(PUBLICATION_STATUS_PARAM);
        }

        deletePaginationParams(searchParams);
      },
    );

  const isDeleted = (statusFilter: CampaignStatusFilter): boolean | undefined => {
    if (statusFilter !== CampaignStatusFilter.All) {
      return statusFilter === CampaignStatusFilter.Archived;
    }

    return;
  };

  const { loading, error, data, paginationProps } = useGetCampaignsWithCampaignComponents({
    campaignFilter: {
      chainIdEquals: chainId,
      search,
      deleted: isDeleted(statusFilter),
      publicationStatusOffersFilter:
        publicationStatusFilter === Publication_Status_Type.All
          ? undefined
          : publicationStatusFilter,
    },
    offerFilter: {
      publicationStatusEquals: publicationStatusFilter,
    },
    paginationInfo,
    onPageChange,
  });

  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const popoverProps = {
    ...bindPopover({
      ...popupState,
      isOpen: popupState.isOpen,
    }),
  };

  // TODO: Think about using Link instead of push here
  const onClick = (campaignId: string) => navigate(campaignRouteCreator.details(campaignId));
  const onEdit = (campaignId: string) => navigate(campaignRouteCreator.update(campaignId));

  const onSearch: SearchFormProps['onSubmit'] = ({ search }) => {
    setSearch(search);
  };

  const onFilterReset = () => {
    setStatusFilter(CampaignStatusFilter.Active);
    setPublicationStatusFilter(Publication_Status_Type.All);
    popoverProps.onClose();
  };

  const onFilterSubmit = ({ status, publicationStatus }: CampaignsFilterFormValues) => {
    setStatusFilter(status);
    setPublicationStatusFilter(publicationStatus);
    popoverProps.onClose();
  };

  const renderActions = () => (
    <Stack direction='row' alignItems='center' gap={1}>
      <SearchForm onSubmit={onSearch} initialValues={{ search }} />
      <FilterButton
        {...bindTrigger(popupState)}
        ref={anchorRef(popupState)}
        showBadge={isFiltersSelected(
          [statusFilter, publicationStatusFilter],
          [CampaignStatusFilter.Active, Publication_Status_Type.All],
        )}
      />
    </Stack>
  );

  return (
    <TableContainer>
      <TableHeader title={messages.title} actions={renderActions()} />
      <CampaignsTable
        campaigns={data}
        loading={loading}
        error={error}
        paginationProps={paginationProps}
        onCampaignClick={onClick}
        onEdit={onEdit}
        showOffers={!!search}
      />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        PaperProps={{ sx: { width: 300 } }}
      >
        <CampaignsFilterForm
          onSubmit={onFilterSubmit}
          onReset={onFilterReset}
          initialValues={{ status: statusFilter, publicationStatus: publicationStatusFilter }}
          showOfferFilters={!!search}
        />
      </Popover>
    </TableContainer>
  );
};
